import {
  ArrowRight,
  BookOpenText,
  CalendarRange,
  ChevronDown,
  CircleCheck,
  CircleDashed,
  CircleSlash,
  FileAudio,
  Lightbulb,
  Link2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { LedgerEffect, LedgerRow, LedgerView, Verdict } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";
import { categoryLabel } from "@/lib/insightCategory";
import { useLedger } from "@/lib/LedgerContext";
import { VERDICT_COLOUR } from "@/lib/verdicts";

/**
 * UI-9 — the insights ledger: everything read out of the sources, where each piece came from,
 * and what it changed in the world view.
 *
 * It lands in M7.3 rather than beside the review queue because it is only now complete: the
 * verdict filter and the "What it changed" column read `belief_verdicts`, which did not exist
 * until M7.1. It is also the other end of UI-7 — a belief's provenance on a subject page links
 * here with `?insight=`, and from a row's source chip the trail carries on into the transcript
 * lines the insight was drawn from.
 *
 * **Read-only, deliberately.** Curation happens at the review gate (INS-4). This is the view
 * you use afterwards to ask "what did last week actually teach the system", which is why a
 * `discarded` insight is legitimately visible here with its verdicts empty — and why nothing
 * on this screen approves, discards or edits anything.
 *
 * Every filter is a Server-side predicate: the URL is the query, one read answers the rows and
 * the navigator panel's counts, and both come from the same object.
 */
export function InsightsPage() {
  const { query, view, loading, problem, focus } = useLedger();
  const [params, setParams] = useSearchParams();

  // The search input is local until it is committed, so typing does not fire a request per
  // keystroke against a corpus that will get large.
  const [draft, setDraft] = useState(query.text ?? "");
  useEffect(() => setDraft(query.text ?? ""), [query.text]);

  function narrow(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key);
    else next.set(key, value);
    // A filter change is a new question, not a new place, so the focused insight goes with it.
    next.delete("insight");
    setParams(next, { replace: true });
  }

  const rows = view?.rows ?? [];

  return (
    <div className="flex flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header className="flex flex-col gap-[var(--space-2)]">
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Insights
        </h1>
        <p className="max-w-[68ch] text-[length:var(--text-md)] text-[var(--text-secondary)]">
          Everything read out of your sources, where each piece came from, and what it changed in your beliefs.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-[var(--space-2)]">
        <span className="inline-flex items-center gap-1.5 text-[length:var(--text-xs)] text-[var(--text-muted)]">
          <CalendarRange size={13} strokeWidth={2} />
          Gained in the last
        </span>

        {(view?.horizons ?? []).map((facet) => (
          <Pill
            key={facet.id}
            label={HORIZONS[facet.id] ?? facet.id}
            count={facet.count}
            active={query.horizon === facet.id}
            onClick={() => narrow("horizon", facet.id === "7d" ? null : facet.id)}
          />
        ))}

        {view && (
          <span className="ml-auto text-[length:var(--text-xs)] text-[var(--text-muted)]">
            {view.from ? `Showing ${formatDate(view.from)} – ${formatDate(view.to)}` : "Showing everything held"}
          </span>
        )}
      </div>

      {view && <Funnel view={view} />}

      <input
        type="search"
        value={draft}
        placeholder="Search insights — text, source or subject…"
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") narrow("q", draft.trim() === "" ? null : draft.trim());
          if (event.key === "Escape") narrow("q", null);
        }}
        onBlur={() => narrow("q", draft.trim() === "" ? null : draft.trim())}
        className="w-full rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-[var(--space-4)] py-[var(--space-3)] text-[length:var(--text-md)] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)] focus:border-[var(--vl-bridge)]"
      />

      <div className="flex flex-wrap items-center gap-[var(--space-2)]">
        {(view?.states ?? []).map((facet) => (
          <Pill
            key={facet.id}
            label={STATES[facet.id] ?? facet.id}
            count={facet.count}
            active={query.state === facet.id}
            onClick={() => narrow("state", facet.id === "all" ? null : facet.id)}
          />
        ))}

        <span className="ml-auto text-[length:var(--text-xs)] text-[var(--text-muted)]">
          {rows.length} {rows.length === 1 ? "insight" : "insights"}
        </span>
      </div>

      {problem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      {loading && view === null ? (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
      ) : rows.length === 0 ? (
        <Empty narrowed={(view?.horizons.find((facet) => facet.id === "all")?.count ?? 0) > 0} />
      ) : (
        <ul className="flex flex-col gap-[var(--space-3)]">
          {rows.map((row) => (
            <li key={row.id}>
              <Row row={row} focused={row.id === focus} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const HORIZONS: Record<string, string> = {
  "7d": "7 days",
  "30d": "30 days",
  "90d": "90 days",
  all: "All time",
};

const STATES: Record<string, string> = {
  all: "All",
  approved: "Approved",
  pending: "Pending review",
  discarded: "Discarded",
};

function Pill({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 rounded-[var(--radius-full)] border px-[var(--space-3)] py-1 text-[length:var(--text-xs)] font-medium transition",
        active
          ? "border-[var(--vl-bridge)] bg-[color-mix(in_oklch,var(--vl-bridge)_16%,transparent)] text-[var(--text-primary)]"
          : "border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--border-default)] hover:text-[var(--text-primary)]",
      )}
    >
      {label}
      <span className="font-mono text-[length:var(--text-2xs)] text-[var(--text-muted)]">{count}</span>
    </button>
  );
}

/**
 * Sources → approved insights → beliefs changed, in the stage colours the pipeline wears
 * everywhere else. It is the shape of the whole product in three numbers, over whichever
 * window the horizon pills are set to.
 */
function Funnel({ view }: { view: LedgerView }) {
  const { sources, approvedInsights, beliefsChanged, subjects } = view.funnel;

  return (
    <div className="flex flex-wrap items-stretch gap-[var(--space-3)]">
      <Card
        icon={FileAudio}
        colour="var(--vl-bridge)"
        count={sources}
        label={sources === 1 ? "source" : "sources"}
        detail="Meetings, calls, documents and notes you brought in."
      />
      <Arrow />
      <Card
        icon={Lightbulb}
        colour="var(--vl-accent-warm)"
        count={approvedInsights}
        label={approvedInsights === 1 ? "approved insight" : "approved insights"}
        detail="Cleaned up, made self-contained, and approved by you."
      />
      <Arrow />
      <Card
        icon={BookOpenText}
        colour="var(--vl-life)"
        count={beliefsChanged}
        label={beliefsChanged === 1 ? "belief changed" : "beliefs changed"}
        detail={`What those insights added up to across ${subjects} ${subjects === 1 ? "subject" : "subjects"}.`}
      />
    </div>
  );
}

function Arrow() {
  return (
    <div className="flex items-center text-[var(--text-muted)]">
      <ArrowRight size={16} strokeWidth={2} aria-hidden />
    </div>
  );
}

function Card({
  icon: Icon,
  colour,
  count,
  label,
  detail,
}: {
  icon: typeof Lightbulb;
  colour: string;
  count: number;
  label: string;
  detail: string;
}) {
  return (
    <div className="flex min-w-[220px] flex-1 flex-col gap-2 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-4)]">
      <span className="flex items-center gap-2">
        <Icon size={15} strokeWidth={2} style={{ color: colour }} aria-hidden />
        <span className="text-[length:var(--text-xl)] font-extrabold" style={{ color: colour }}>
          {count}
        </span>
        <span className="text-[length:var(--text-sm)] font-medium text-[var(--text-primary)]">{label}</span>
      </span>
      <span className="text-[length:var(--text-xs)] text-[var(--text-secondary)]">{detail}</span>
    </div>
  );
}

/**
 * One insight, its source, and the beliefs it moved.
 *
 * The row is the same whatever the review state, because the ledger is a record of what
 * happened rather than of what stands: a discarded insight has no verdicts and says so, and an
 * approved one that changed nothing looks exactly like it — which is the honest picture of a
 * week where the system learned less than it read.
 */
function Row({ row, focused }: { row: LedgerRow; focused: boolean }) {
  const [open, setOpen] = useState(focused);
  const anchor = useRef<HTMLElement>(null);

  // Arrived from a belief's provenance (UI-7): open the disclosure and bring the row into view,
  // because a ledger of two hundred rows is not a place somebody should have to go looking.
  useEffect(() => {
    // Optional-called: not every environment the app is rendered in implements it, and
    // failing to scroll is not worth taking the page down for.
    if (focused) anchor.current?.scrollIntoView?.({ block: "center" });
  }, [focused]);

  const Icon = row.reviewState === "approved" ? CircleCheck : row.reviewState === "discarded" ? CircleSlash : CircleDashed;
  const sourceHref =
    row.sourceKind === "recording"
      ? `/recordings/${row.sourceMaterialId}?insight=${row.id}`
      : `/sources/${row.sourceMaterialId}?insight=${row.id}`;

  const subjects = new Set(row.effects.map((effect) => effect.subjectSlug)).size;

  return (
    <article
      ref={anchor}
      className={cn(
        "flex flex-col gap-[var(--space-2)] rounded-[var(--radius-xl)] border bg-[var(--surface-glass)] px-[var(--space-6)] py-[var(--space-4)] transition",
        focused ? "border-[var(--vl-bridge)]" : "border-[var(--border-subtle)]",
      )}
    >
      <div className="flex items-start gap-[var(--space-3)]">
        <Icon
          size={16}
          strokeWidth={2}
          aria-hidden
          className={cn(
            "mt-0.5 shrink-0",
            row.reviewState === "approved" ? "text-[var(--success)]" : "text-[var(--text-muted)]",
          )}
        />

        <p
          className={cn(
            "flex-1 text-[length:var(--text-md)] leading-relaxed",
            row.reviewState === "discarded" ? "text-[var(--text-muted)]" : "text-[var(--text-primary)]",
          )}
        >
          {row.text}
        </p>

        <span
          className={cn(
            "shrink-0 text-[length:var(--text-xs)] font-semibold",
            row.reviewState === "approved" ? "text-[var(--success)]" : "text-[var(--text-muted)]",
          )}
        >
          {STATES[row.reviewState]}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-[var(--space-2)] pl-[calc(16px+var(--space-3))]">
        <Link
          to={sourceHref}
          className="inline-flex items-center gap-1.5 rounded-[var(--radius-full)] border border-[var(--border-subtle)] bg-[var(--surface-2)] px-2.5 py-0.5 text-[length:var(--text-2xs)] text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
        >
          <Link2 size={11} strokeWidth={2} />
          {row.sourceTitle ?? "Untitled source"}
          {row.sourceDate && <> · {formatDate(row.sourceDate)}</>}
        </Link>

        <span className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
          {categoryLabel(row.category)}
        </span>

        {row.effects.length > 0 ? (
          <>
            <ArrowRight size={12} strokeWidth={2} className="text-[var(--text-muted)]" aria-hidden />
            <span className="text-[length:var(--text-xs)] font-medium text-[var(--text-primary)]">
              Touched {row.effects.length} {row.effects.length === 1 ? "belief" : "beliefs"} across {subjects}{" "}
              {subjects === 1 ? "subject" : "subjects"}
            </span>

            {[...new Set(row.effects.map((effect) => effect.verdict))].map((verdict) => (
              <span
                key={verdict}
                className="inline-flex items-center gap-1 text-[length:var(--text-2xs)]"
                style={{ color: VERDICT_COLOUR[verdict as Verdict] }}
              >
                <span
                  className="inline-block size-1.5 rounded-full"
                  style={{ background: VERDICT_COLOUR[verdict as Verdict] }}
                />
                {verdict}
              </span>
            ))}

            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen((value) => !value)}
              className="ml-auto inline-flex items-center gap-1.5 text-[length:var(--text-xs)] font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
            >
              What it changed
              <ChevronDown size={13} strokeWidth={2} className={cn("transition-transform", open && "rotate-180")} />
            </button>
          </>
        ) : (
          <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
            {row.reviewState === "discarded"
              ? "Discarded in review — nothing was believed on the strength of it."
              : row.reviewState === "pending"
                ? "Waiting on you — nothing is believed on the strength of it yet."
                : "Changed nothing in the world view."}
          </span>
        )}
      </div>

      {open && row.effects.length > 0 && (
        <ul className="ml-[calc(16px+var(--space-3))] flex flex-col gap-[var(--space-2)] border-l border-[var(--border-subtle)] pl-[var(--space-4)]">
          {row.effects.map((effect) => (
            <li key={effect.beliefId}>
              <Effect effect={effect} />
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

/** One belief this insight moved: the verdict, the page and section it lives on, and how sure
 * the system now is. The subject link carries the anchor, so it lands on the section rather
 * than at the top of a long page. */
function Effect({ effect }: { effect: LedgerEffect }) {
  return (
    <div className="flex flex-wrap items-center gap-[var(--space-3)]">
      <span
        className="w-[86px] shrink-0 text-[length:var(--text-xs)] font-semibold"
        style={{ color: VERDICT_COLOUR[effect.verdict] }}
      >
        {effect.verdict}
      </span>

      <Link
        to={`/world-view/${effect.subjectSlug}#${effect.anchor}`}
        className="text-[length:var(--text-sm)] text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
      >
        {effect.subjectTitle} · {effect.section}
      </Link>

      {!effect.current && (
        <span className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
          no longer current
        </span>
      )}

      {effect.confidence !== null && (
        <span className="ml-auto font-mono text-[length:var(--text-2xs)] text-[var(--text-muted)]">
          confidence {effect.confidence.toFixed(2)}
        </span>
      )}
    </div>
  );
}

/**
 * Two different nothings. A corpus with nothing in it at all is waiting on the pipeline; one
 * merely filtered down to nothing is a filter with no matches, and telling somebody to add a
 * source there would be wrong.
 */
function Empty({ narrowed }: { narrowed: boolean }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-[var(--radius-xl)] border border-dashed border-[var(--border-default)] p-[var(--space-8)] text-center">
      <Lightbulb size={22} strokeWidth={1.75} className="text-[var(--text-muted)]" aria-hidden />

      {narrowed ? (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          Nothing matches those filters.{" "}
          <Link to="/insights?horizon=all" className="underline underline-offset-2 hover:text-[var(--text-primary)]">
            Show everything held
          </Link>
          .
        </p>
      ) : (
        <p className="max-w-[52ch] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          Nothing has been read out of a source yet. Insights appear here as your recordings and documents are
          processed, whether or not you go on to approve them.
        </p>
      )}
    </div>
  );
}
